import * as THREE from 'three';
import { BinaryInstancedSurface } from './BinaryInstancedSurface.js';
import { BINARY_DEFAULTS } from './binaryConfig.js';

const _center = new THREE.Vector3();
const _normal = new THREE.Vector3();
const _tangentU = new THREE.Vector3();
const _tangentV = new THREE.Vector3();

/**
 * Reusable digital prop: geometry made from instanced 0/1 glyphs.
 *
 * DigitalObject
 *   ├── geometry (instanced glyph quads)
 *   ├── binary material (shared)
 *   └── animation (material time + optional updates)
 */
export class DigitalObject {
  /**
   * @param {{
   *   material: import('./BinaryMaterial.js').BinaryMaterial;
   *   shape: 'box' | 'wall' | 'pillar' | 'building' | 'facade';
   *   size: [number, number, number];
   *   position?: [number, number, number];
   *   cellsPerMeter?: number;
   *   seed?: number;
   *   name?: string;
   *   cellIdBase?: number;
   * }} options
   */
  constructor({
    material,
    shape,
    size,
    position = [0, 0, 0],
    cellsPerMeter = BINARY_DEFAULTS.cellsPerMeter,
    seed = 1,
    name = 'DigitalObject',
    cellIdBase = 0,
  }) {
    this.material = material;
    this.shape = shape;
    this.size = size;
    this.position = position;
    this.cellsPerMeter = cellsPerMeter;
    this.seed = seed;
    this.name = name;
    this.cellIdBase = cellIdBase;
    this.cellsUsed = 0;

    /** @type {BinaryInstancedSurface[]} */
    this.surfaces = [];
    this.group = new THREE.Group();
    this.group.name = name;
    this.group.position.set(position[0], position[1], position[2]);

    this._buildSurfaces();
    this._syncMaterialColumns();
  }

  get instanceCount() {
    return this.surfaces.reduce((sum, s) => sum + s.instanceCount, 0);
  }

  get drawCalls() {
    return this.surfaces.length;
  }

  /** Animation is driven by the shared BinaryMaterial owner (e.g. BinaryShowcase). */
  update(_deltaSeconds) {}

  dispose() {
    for (const surface of this.surfaces) surface.dispose();
    this.surfaces = [];
    this.group.clear();
  }

  /** Axis-aligned bounds in world space (for collision). */
  getCollider() {
    const [sx, sy, sz] = this.size;
    const [x, y, z] = this.position;
    return {
      min: new THREE.Vector3(x - sx / 2, y - sy / 2, z - sz / 2),
      max: new THREE.Vector3(x + sx / 2, y + sy / 2, z + sz / 2),
    };
  }

  _buildSurfaces() {
    const [sx, sy, sz] = this.size;
    const halfX = sx / 2;
    const halfY = sy / 2;
    const halfZ = sz / 2;

    /** @type {Array<{ width: number; height: number; center: THREE.Vector3; normal: THREE.Vector3; tangentU: THREE.Vector3; tangentV: THREE.Vector3; }>} */
    let faces = [];

    if (this.shape === 'wall') {
      faces = [
        makeFace(0, 0, halfZ, 0, 0, 1, sx, sy, 1, 0, 0, 0, 1, 0),
        makeFace(0, 0, -halfZ, 0, 0, -1, sx, sy, -1, 0, 0, 0, 1, 0),
      ];
    } else if (this.shape === 'pillar') {
      faces = [
        makeFace(halfX, 0, 0, 1, 0, 0, sz, sy, 0, 0, -1, 0, 1, 0),
        makeFace(-halfX, 0, 0, -1, 0, 0, sz, sy, 0, 0, 1, 0, 1, 0),
        makeFace(0, 0, halfZ, 0, 0, 1, sx, sy, 1, 0, 0, 0, 1, 0),
        makeFace(0, 0, -halfZ, 0, 0, -1, sx, sy, -1, 0, 0, 0, 1, 0),
      ];
    } else if (this.shape === 'facade') {
      faces = [
        makeFace(halfX, 0, 0, 1, 0, 0, sz, sy, 0, 0, -1, 0, 1, 0),
        makeFace(-halfX, 0, 0, -1, 0, 0, sz, sy, 0, 0, 1, 0, 1, 0),
        makeFace(0, 0, halfZ, 0, 0, 1, sx, sy, 1, 0, 0, 0, 1, 0),
        makeFace(0, 0, -halfZ, 0, 0, -1, sx, sy, -1, 0, 0, 0, 1, 0),
      ];
    } else if (this.shape === 'building') {
      faces = [
        makeFace(0, halfY, 0, 0, 1, 0, sx, sz, 1, 0, 0, 0, 0, -1),
        makeFace(halfX, 0, 0, 1, 0, 0, sz, sy, 0, 0, -1, 0, 1, 0),
        makeFace(-halfX, 0, 0, -1, 0, 0, sz, sy, 0, 0, 1, 0, 1, 0),
        makeFace(0, 0, halfZ, 0, 0, 1, sx, sy, 1, 0, 0, 0, 1, 0),
        makeFace(0, 0, -halfZ, 0, 0, -1, sx, sy, -1, 0, 0, 0, 1, 0),
      ];
    } else {
      faces = [
        makeFace(0, halfY, 0, 0, 1, 0, sx, sz, 1, 0, 0, 0, 0, -1),
        makeFace(0, -halfY, 0, 0, -1, 0, sx, sz, 1, 0, 0, 0, 0, 1),
        makeFace(halfX, 0, 0, 1, 0, 0, sz, sy, 0, 0, -1, 0, 1, 0),
        makeFace(-halfX, 0, 0, -1, 0, 0, sz, sy, 0, 0, 1, 0, 1, 0),
        makeFace(0, 0, halfZ, 0, 0, 1, sx, sy, 1, 0, 0, 0, 1, 0),
        makeFace(0, 0, -halfZ, 0, 0, -1, sx, sy, -1, 0, 0, 0, 1, 0),
      ];
    }

    let faceSeed = this.seed;
    let cellOffset = this.cellIdBase;
    for (const def of faces) {
      const surface = new BinaryInstancedSurface({
        material: this.material,
        width: def.width,
        height: def.height,
        cellsPerMeter: this.cellsPerMeter,
        center: def.center,
        normal: def.normal,
        tangentU: def.tangentU,
        tangentV: def.tangentV,
        seed: faceSeed,
        cellIdOffset: cellOffset,
      });
      faceSeed += 97;
      cellOffset += surface.instanceCount;
      this.surfaces.push(surface);
      this.group.add(surface.mesh);
    }
    this.cellsUsed = cellOffset - this.cellIdBase;
  }

  _syncMaterialColumns() {
    const maxColumns = this.surfaces.reduce(
      (max, s) => Math.max(max, s.columns),
      1,
    );
    this.material.setColumns(maxColumns);
  }
}

function makeFace(
  cx,
  cy,
  cz,
  nx,
  ny,
  nz,
  width,
  height,
  tux,
  tuy,
  tuz,
  tvx,
  tvy,
  tvz,
) {
  return {
    width,
    height,
    center: _center.set(cx, cy, cz).clone(),
    normal: _normal.set(nx, ny, nz).clone(),
    tangentU: _tangentU.set(tux, tuy, tuz).clone(),
    tangentV: _tangentV.set(tvx, tvy, tvz).clone(),
  };
}
